/**
 * TimeSlotSelector - Select available dates and time slots for a class
 *
 * Business Rules:
 * - Classes available Saturday, Sunday & Monday
 * - Saturday & Sunday: 2-session toggle cards (Session 1 + Session 2)
 * - Monday: Multi-slot time grid with per-slot capacity
 *
 * Date view modes:
 * - 'auto': List view for ≤15 available dates, calendar for >15
 * - 'list': Always show date list
 * - 'calendar': Always show calendar
 */

import { useState, useEffect, useMemo, useRef } from 'react';
import type { TimeSlot, ClassTimeSlot, TimeSlotSelection, DateViewMode, PublicSystemSettings } from '../../api/events';
import { CLASS_TIME_SLOTS } from '../../api/events';
import { resolveSessionTimes, formatTimeDisplay, isMatineeThresholdTriggered, type SessionMode } from '@sda/shared';

/**
 * Format a date as YYYY-MM-DD in local timezone
 * This avoids timezone issues that occur with toISOString()
 */
function formatLocalDate(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

const LEVEL_OPTIONS = [
  { value: 'type_a', label: 'Type A: Beginner', shortLabel: 'Beginner', ageRange: 'Ages 9+' },
  { value: 'type_b', label: 'Type B: Intermediate', shortLabel: 'Intermediate', ageRange: 'Ages 9-12' },
  { value: 'type_c', label: 'Type C: Int/Adv', shortLabel: 'Int/Advanced', ageRange: 'Ages 12+' },
];

interface TimeSlotSelectorProps {
  availability: TimeSlot[];
  selectedDate?: string;
  selectedTimeSlot?: TimeSlotSelection;
  selectedLevels?: { session1?: string; session2?: string };
  onSelect: (date: string, timeSlot: TimeSlotSelection | undefined, levels?: { session1?: string; session2?: string }) => void;
  dateViewMode?: DateViewMode;
  systemSettings?: PublicSystemSettings | null;
  matineeTime?: string | null;
  startTimePreference?: 'early' | 'default' | 'late';
  forceSimultaneous?: boolean;
  isSimultaneousTriggered?: boolean;
  sundayMatineeTime?: string;
  mondayStartTime?: string;
}

const LIST_VIEW_THRESHOLD = 15;

/** Fallback slot labels shown while system settings are loading. */
const LOADING_SLOT_LABELS: Record<ClassTimeSlot, { label: string; time: string }> = {
  '9:00 AM': { label: 'Class Slot 1', time: 'Loading...' },
  '11:00 AM': { label: 'Class Slot 2', time: 'Loading...' },
};

export function TimeSlotSelector({
  availability,
  selectedDate,
  selectedTimeSlot,
  selectedLevels,
  onSelect,
  dateViewMode = 'auto',
  systemSettings,
  matineeTime,
  startTimePreference = 'default',
  forceSimultaneous = false,
  isSimultaneousTriggered: isSimultaneousTriggeredProp,
  sundayMatineeTime = '13:00:00',
  mondayStartTime = '09:00:00',
}: TimeSlotSelectorProps) {
  const [level1, setLevel1] = useState('');
  const [level2, setLevel2] = useState('');
  // Multi-select toggle state
  const [session1On, setSession1On] = useState(false);
  const [session2On, setSession2On] = useState(false);
  const levelSectionRef = useRef<HTMLDivElement>(null);
  const timeSlotSectionRef = useRef<HTMLDivElement>(null);
  const isExplicitSlotClick = useRef(false);

  // Get the day_type for the selected date from availability data
  const selectedDayType = useMemo((): 'saturday' | 'sunday' | 'monday' => {
    if (!selectedDate) return 'saturday';
    const slot = availability.find((s) => s.date === selectedDate);
    return slot?.day_type ?? 'saturday';
  }, [selectedDate, availability]);

  // Compute dynamic time slot labels from system settings
  // Session times vary by day_type: Sunday uses matinee (simultaneous), Monday uses 9am start (sequential)
  const TIME_SLOT_LABELS = useMemo((): Record<ClassTimeSlot, { label: string; time: string }> => {
    if (!systemSettings) return LOADING_SLOT_LABELS;

    try {
      // Per-day session time configuration
      let sessionStartOverride: string | undefined;
      let effectiveMatinee: string | null;
      let effectiveForceSimultaneous: boolean;

      if (selectedDayType === 'sunday') {
        effectiveMatinee = sundayMatineeTime || '13:00:00';
        effectiveForceSimultaneous = forceSimultaneous; // Sunday uses same matinee threshold logic as Saturday
        sessionStartOverride = undefined; // Use system default start
      } else if (selectedDayType === 'monday') {
        effectiveMatinee = null; // No matinee constraint on Monday
        effectiveForceSimultaneous = false;
        sessionStartOverride = mondayStartTime || '09:00:00';
      } else {
        effectiveMatinee = matineeTime || null; // Saturday uses tour stop matinee
        effectiveForceSimultaneous = forceSimultaneous;
      }

      // Derive mode from selectedTimeSlot + effectiveForceSimultaneous
      let slotMode: SessionMode;
      if (selectedTimeSlot === 'both') {
        slotMode = effectiveForceSimultaneous ? 'simultaneous' : 'back_to_back';
      } else if (selectedTimeSlot === '11:00 AM') {
        slotMode = 'second_slot';
      } else {
        // '9:00 AM' or undefined → single
        slotMode = 'single';
      }

      const resolved = resolveSessionTimes(
        {
          defaultSessionStartTime: sessionStartOverride || systemSettings.default_session_start_time,
          matineeTime: effectiveMatinee,
          sessionDurationMinutes: systemSettings.session_duration_minutes,
          matineeThresholdTime: systemSettings.matinee_threshold_time,
          defaultMatineeTime: systemSettings.default_matinee_time,
        },
        startTimePreference,
        slotMode,
      );

      return {
        '9:00 AM': {
          label: 'Class Slot 1',
          time: `${formatTimeDisplay(resolved.session1.start)} - ${formatTimeDisplay(resolved.session1.end)}`,
        },
        '11:00 AM': {
          label: 'Class Slot 2',
          time: `${formatTimeDisplay(resolved.session2.start)} - ${formatTimeDisplay(resolved.session2.end)}`,
        },
      };
    } catch {
      return LOADING_SLOT_LABELS;
    }
  }, [systemSettings, matineeTime, startTimePreference, forceSimultaneous, selectedDayType, sundayMatineeTime, mondayStartTime, selectedTimeSlot]);

  // Check if matinee threshold is triggered (simultaneous sessions)
  // Use prop if provided (computed in parent), otherwise compute locally
  const isSimultaneousTriggered = useMemo(() => {
    if (isSimultaneousTriggeredProp !== undefined) return isSimultaneousTriggeredProp;
    if (!systemSettings) return false;
    try {
      return isMatineeThresholdTriggered(
        matineeTime || null,
        systemSettings.matinee_threshold_time,
        systemSettings.default_matinee_time
      );
    } catch {
      return false;
    }
  }, [isSimultaneousTriggeredProp, systemSettings, matineeTime]);

  // Sync internal state from props (for pre-filling from parent)
  useEffect(() => {
    setLevel1(selectedLevels?.session1 || '');
    setLevel2(selectedLevels?.session2 || '');
  }, [selectedLevels?.session1, selectedLevels?.session2]);

  // Sync toggle state from selectedTimeSlot prop
  useEffect(() => {
    if (selectedTimeSlot === 'both') {
      setSession1On(true);
      setSession2On(true);
    } else if (selectedTimeSlot === '9:00 AM') {
      setSession1On(true);
      setSession2On(false);
    } else if (selectedTimeSlot === '11:00 AM') {
      setSession1On(false);
      setSession2On(true);
    } else {
      setSession1On(false);
      setSession2On(false);
    }
    // Sync Monday slot grid selection when a custom slot key is pre-filled from URL
    if (selectedTimeSlot && selectedTimeSlot !== 'both' && selectedTimeSlot !== '9:00 AM' && selectedTimeSlot !== '11:00 AM') {
      setSelectedMondaySlot(selectedTimeSlot);
    }
  }, [selectedTimeSlot]);

  // Scroll to time slot section when date is selected (not on session toggle)
  useEffect(() => {
    if (selectedTimeSlot && !isExplicitSlotClick.current) {
      setTimeout(() => {
        timeSlotSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 100);
    }
    isExplicitSlotClick.current = false;
  }, [selectedTimeSlot]);
  const [viewMonth, setViewMonth] = useState(() => {
    // Start with the first available date's month or current month
    const firstAvailable = availability.find((slot) => slot.available);
    if (firstAvailable) {
      return new Date(firstAvailable.date + 'T00:00:00');
    }
    return new Date();
  });

  // Group availability by date
  const availabilityByDate = useMemo(() => {
    const map = new Map<string, TimeSlot[]>();
    availability.forEach((slot) => {
      const existing = map.get(slot.date) || [];
      map.set(slot.date, [...existing, slot]);
    });
    return map;
  }, [availability]);

  // Get date-level remaining/sold_out (aggregate across slots for a date)
  const dateSummary = useMemo(() => {
    const map = new Map<string, { remaining: number; total: number; sold_out: boolean; is_closed: boolean }>();
    availabilityByDate.forEach((slots, date) => {
      // Use the first slot's values as representative for the date
      const first = slots[0];
      if (first) {
        map.set(date, {
          remaining: first.remaining,
          total: first.total,
          sold_out: first.sold_out,
          is_closed: slots.every((s) => s.is_closed === true),
        });
      }
    });
    return map;
  }, [availabilityByDate]);

  // Get unique dates that have any available slot OR are sold out (to show "Sold Out" badge)
  const availableDates = useMemo(() => {
    const dates = new Set<string>();
    availability.forEach((slot) => {
      if (slot.available) dates.add(slot.date);
    });
    return Array.from(dates).sort();
  }, [availability]);

  // All dates (including sold out) for list view
  const allDates = useMemo(() => {
    const dates = new Set<string>();
    availability.forEach((slot) => {
      dates.add(slot.date);
    });
    return Array.from(dates).sort();
  }, [availability]);

  // Group dates by week (Saturday as week key) for list view
  const weekGroups = useMemo(() => {
    const groups: { weekLabel: string; dates: string[] }[] = [];
    const groupMap = new Map<string, string[]>();
    const groupOrder: string[] = [];

    for (const dateStr of allDates) {
      const dateObj = new Date(dateStr + 'T00:00:00');
      const dow = dateObj.getDay();
      // Find the Saturday of this week (Sat=6, Sun=0 -> Sat is day-1, Mon=1 -> Sat is day-2)
      const satOffset = dow === 6 ? 0 : dow === 0 ? -1 : dow === 1 ? -2 : -dow + 6;
      const satDate = new Date(dateObj);
      satDate.setDate(satDate.getDate() + satOffset);
      const satKey = formatLocalDate(satDate);

      if (!groupMap.has(satKey)) {
        groupMap.set(satKey, []);
        groupOrder.push(satKey);
      }
      groupMap.get(satKey)!.push(dateStr);
    }

    for (const satKey of groupOrder) {
      const satDate = new Date(satKey + 'T00:00:00');
      const weekLabel = `Week of ${satDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`;
      groups.push({ weekLabel, dates: groupMap.get(satKey)! });
    }

    return groups;
  }, [allDates]);

  // Determine which view to use
  const useListView =
    dateViewMode === 'list' ||
    (dateViewMode !== 'calendar' && availableDates.length <= LIST_VIEW_THRESHOLD);

  // Get calendar days for current view month
  const calendarDays = useMemo(() => {
    const year = viewMonth.getFullYear();
    const month = viewMonth.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);

    const days: Array<{ date: Date; isCurrentMonth: boolean; slots: TimeSlot[] }> = [];

    // Add days from previous month to fill first week
    const startPadding = firstDay.getDay();
    for (let i = startPadding - 1; i >= 0; i--) {
      const date = new Date(year, month, -i);
      days.push({
        date,
        isCurrentMonth: false,
        slots: [],
      });
    }

    // Add days of current month
    for (let day = 1; day <= lastDay.getDate(); day++) {
      const date = new Date(year, month, day);
      const dateStr = formatLocalDate(date);
      days.push({
        date,
        isCurrentMonth: true,
        slots: availabilityByDate.get(dateStr) || [],
      });
    }

    // Add days from next month to fill last week
    const endPadding = 6 - lastDay.getDay();
    for (let i = 1; i <= endPadding; i++) {
      const date = new Date(year, month + 1, i);
      days.push({
        date,
        isCurrentMonth: false,
        slots: [],
      });
    }

    return days;
  }, [viewMonth, availabilityByDate]);

  // Get available time slots for selected date
  const selectedDateSlots = useMemo(() => {
    if (!selectedDate) return [];
    return availabilityByDate.get(selectedDate) || [];
  }, [selectedDate, availabilityByDate]);

  const prevMonth = () => {
    setViewMonth(new Date(viewMonth.getFullYear(), viewMonth.getMonth() - 1, 1));
  };

  const nextMonth = () => {
    setViewMonth(new Date(viewMonth.getFullYear(), viewMonth.getMonth() + 1, 1));
  };

  const formatMonthYear = (date: Date) => {
    return date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
  };

  const isDateSelectable = (slots: TimeSlot[]) => {
    return slots.some((slot) => slot.available);
  };

  // State for the selected Monday time slot key (e.g. "10:30 AM")
  const [selectedMondaySlot, setSelectedMondaySlot] = useState<string | null>(null);
  const [mondayLevel, setMondayLevel] = useState('');

  // Reset Monday state when date changes
  useEffect(() => {
    setSelectedMondaySlot(null);
    setMondayLevel('');
  }, [selectedDate]);

  // Get Monday slots for the selected date
  const mondaySlots = useMemo(() => {
    if (!selectedDate || selectedDayType !== 'monday') return [];
    return (availabilityByDate.get(selectedDate) || []);
  }, [selectedDate, selectedDayType, availabilityByDate]);

  const isDateClosed = (slots: TimeSlot[]) => {
    return slots.length > 0 && slots.every((s) => s.is_closed === true);
  };

  const handleDateClick = (dateStr: string, slots: TimeSlot[]) => {
    // Allow clicking closed dates (to show waitlist), but block truly unavailable dates
    if (!isDateSelectable(slots) && !isDateClosed(slots)) return;
    const dayType = slots[0]?.day_type ?? 'saturday';
    if (dayType === 'monday') {
      // For Monday: select date but don't auto-select a time slot — user picks from grid
      onSelect(dateStr, undefined);
    } else {
      const firstAvailable = slots.find((s) => s.available);
      if (firstAvailable) {
        onSelect(dateStr, firstAvailable.time_slot as TimeSlotSelection);
      } else {
        // Closed or sold-out date — select without a time slot
        onSelect(dateStr, undefined);
      }
    }
  };

  const handleMondaySlotSelect = (slot: TimeSlot) => {
    if (!selectedDate || slot.remaining <= 0) return;
    setSelectedMondaySlot(slot.time_slot);
    setMondayLevel(''); // Reset level when changing slot
    onSelect(selectedDate, slot.time_slot as TimeSlotSelection);
  };

  const handleMondayLevelSelect = (levelValue: string) => {
    if (!selectedDate || !selectedMondaySlot) return;
    setMondayLevel(levelValue);
    onSelect(selectedDate, selectedMondaySlot as TimeSlotSelection, { session1: levelValue });
  };

  return (
    <div className="space-y-6">
      {/* Date Selection: List or Calendar */}
      {useListView ? (
        /* List View — grouped by week when multiple weeks */
        <div className="space-y-4">
          {weekGroups.map((group) => (
            <div key={group.weekLabel}>
              {weekGroups.length > 1 && (
                <div className="text-xs font-semibold text-warm-500 uppercase tracking-wide mb-2 px-1">
                  {group.weekLabel}
                </div>
              )}
              <div className="space-y-2">
                {group.dates.map((dateStr) => {
                  const slots = availabilityByDate.get(dateStr) || [];
                  const summary = dateSummary.get(dateStr);
                  const isSoldOut = summary?.sold_out || (summary?.remaining ?? 1) <= 0;
                  const isClosed = summary?.is_closed ?? false;
                  const isSelected = dateStr === selectedDate;
                  const dateObj = new Date(dateStr + 'T00:00:00');
                  const label = dateObj.toLocaleDateString('en-US', {
                    weekday: 'short',
                    month: 'long',
                    day: 'numeric',
                    year: 'numeric',
                  });
                  // Get day_type from first slot for this date
                  const slotDayType = slots[0]?.day_type ?? 'saturday';
                  // Day-type color tinting
                  const dayTintClass =
                    isClosed ? 'bg-amber-50/60' :
                    slotDayType === 'sunday' ? 'bg-success-50/50' :
                    slotDayType === 'monday' ? 'bg-amber-50/50' : '';

                  return (
                    <button
                      key={dateStr}
                      type="button"
                      disabled={isSoldOut && !isClosed}
                      onClick={() => handleDateClick(dateStr, slots)}
                      className={`
                        w-full text-left px-4 py-3 rounded-lg border-2 transition-all
                        ${isSoldOut && !isClosed
                          ? 'border-warm-200 bg-warm-50 cursor-not-allowed opacity-75'
                          : isClosed
                            ? isSelected
                              ? 'border-amber-400 bg-amber-50 ring-2 ring-amber-200'
                              : 'border-amber-200 bg-amber-50/60 hover:border-amber-300 cursor-pointer'
                            : isSelected
                              ? 'border-primary-500 bg-primary-50 ring-2 ring-primary-200'
                              : `border-warm-200 hover:border-primary-300 hover:bg-warm-50 ${dayTintClass}`
                        }
                      `}
                    >
                      <div className="flex items-center justify-between">
                        <span className={`font-medium ${isSoldOut && !isClosed ? 'text-warm-400' : isClosed ? (isSelected ? 'text-amber-800' : 'text-amber-700') : isSelected ? 'text-primary-700' : 'text-warm-900'}`}>
                          {label}
                        </span>
                        {isClosed ? (
                          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-700">
                            Inquiry Only
                          </span>
                        ) : isSoldOut ? (
                          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-danger-100 text-danger-700">
                            Sold Out
                          </span>
                        ) : summary && summary.remaining <= 5 ? (
                          <span className="text-xs font-medium text-warm-500">
                            {summary.remaining} spot{summary.remaining !== 1 ? 's' : ''} left
                          </span>
                        ) : null}
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          ))}
          {allDates.length === 0 && (
            <p className="text-warm-500 text-center py-4">No available dates found.</p>
          )}
        </div>
      ) : (
        /* Calendar View */
        <div className="border border-warm-200 rounded-lg overflow-hidden">
          {/* Month Navigation */}
          <div className="flex items-center justify-between px-4 py-3 bg-warm-50 border-b border-warm-200">
            <button
              type="button"
              onClick={prevMonth}
              className="p-1 rounded hover:bg-warm-200 transition-colors"
              aria-label="Previous month"
            >
              <svg className="h-5 w-5 text-warm-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>
            <span className="font-semibold text-warm-900">
              {formatMonthYear(viewMonth)}
            </span>
            <button
              type="button"
              onClick={nextMonth}
              className="p-1 rounded hover:bg-warm-200 transition-colors"
              aria-label="Next month"
            >
              <svg className="h-5 w-5 text-warm-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </button>
          </div>

          {/* Day Headers */}
          <div className="grid grid-cols-7 text-center text-xs font-medium text-warm-500 bg-warm-50 border-b border-warm-200">
            {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((day) => (
              <div key={day} className="py-2">
                {day}
              </div>
            ))}
          </div>

          {/* Calendar Grid */}
          <div className="grid grid-cols-7">
            {calendarDays.map(({ date, isCurrentMonth, slots }, index) => {
              const dateStr = formatLocalDate(date);
              const isSelected = dateStr === selectedDate;
              const hasAvailability = isDateSelectable(slots);
              const summary = dateSummary.get(dateStr);
              const isSoldOut = slots.length > 0 && (summary?.sold_out || (summary?.remaining ?? 1) <= 0);
              const isClosed = summary?.is_closed ?? false;
              const isToday = dateStr === formatLocalDate(new Date());
              const isClickable = hasAvailability || isClosed;

              return (
                <button
                  key={index}
                  type="button"
                  disabled={!isClickable}
                  onClick={() => handleDateClick(dateStr, slots)}
                  className={`
                    relative h-12 text-sm transition-colors
                    ${!isCurrentMonth ? 'text-warm-300' : 'text-warm-900'}
                    ${isClickable ? 'hover:bg-primary-50 cursor-pointer' : 'cursor-not-allowed'}
                    ${isSelected && isClosed ? 'bg-amber-400 text-white hover:bg-amber-500' : ''}
                    ${isSelected && !isClosed ? 'bg-primary-500 text-white hover:bg-primary-600' : ''}
                    ${isToday && !isSelected ? 'font-bold' : ''}
                    ${isSoldOut && !isClosed && !isSelected ? 'text-warm-400' : ''}
                  `}
                >
                  <span>{date.getDate()}</span>
                  {isClosed && !isSelected ? (
                    <span className="absolute bottom-1 left-1/2 -translate-x-1/2 w-1.5 h-1.5 rounded-full bg-amber-400" />
                  ) : isSoldOut && !isClosed && !isSelected ? (
                    <span className="absolute bottom-0.5 left-1/2 -translate-x-1/2 text-[8px] font-medium text-danger-500 leading-none">
                      Full
                    </span>
                  ) : hasAvailability && !isSelected ? (
                    <span className="absolute bottom-1 left-1/2 -translate-x-1/2 w-1.5 h-1.5 rounded-full bg-primary-500" />
                  ) : null}
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Time Slot Selection */}
      {selectedDate && (() => {
        const isSelectedDateClosed = dateSummary.get(selectedDate)?.is_closed ?? false;

        return (
        <div ref={timeSlotSectionRef} className="space-y-3">
          {isSelectedDateClosed ? (
            <div className="border-2 border-amber-200 rounded-xl bg-amber-50 p-5 mt-4" data-testid="closed-date-message">
              <div className="flex items-start gap-3">
                <svg className="h-6 w-6 text-amber-500 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <div>
                  <h4 className="font-semibold text-warm-900">Direct booking is closed for this date</h4>
                  <p className="text-sm text-warm-600 mt-1">
                    We are too close to the class date for direct booking. Submit a request and we'll check if we can accommodate your booking.
                  </p>
                </div>
              </div>
            </div>
          ) : (
          <>
          <div>
            <h4 className="font-medium text-warm-900">
              {selectedDayType === 'monday'
                ? 'Select a time slot for '
                : 'Select time slot and class level for '}
              {new Date(selectedDate + 'T12:00:00').toLocaleDateString('en-US', {
                weekday: 'short',
                month: 'long',
                day: 'numeric',
              })}
            </h4>
            {(selectedDayType === 'saturday' || selectedDayType === 'sunday') && (
              <p className="text-sm text-warm-500 mt-1">
                {(dateSummary.get(selectedDate)?.remaining ?? Infinity) < 2
                  ? 'Only single class slot available for this date'
                  : 'Select one or both class slots'}
              </p>
            )}
            {selectedDayType === 'monday' && (
              <p className="text-sm text-warm-500 mt-1">
                Choose from available time slots below
              </p>
            )}
          </div>

          {/* ============ SATURDAY & SUNDAY — 2-session toggle cards ============ */}
          {(selectedDayType === 'saturday' || selectedDayType === 'sunday') && (
            <>
              {/* Single-session-only notice when only 1 faculty spot remains */}
              {(dateSummary.get(selectedDate)?.remaining ?? Infinity) < 2 && (
                <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg" data-testid="single-session-only-notice">
                  <div className="flex items-start gap-2">
                    <svg className="h-5 w-5 text-amber-500 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <div className="text-sm text-amber-800">
                      <strong>1 spot left</strong> — single class slot only
                    </div>
                  </div>
                </div>
              )}

              {/* Simultaneous sessions notice */}
              {isSimultaneousTriggered && (
                <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
                  <div className="flex items-start gap-2">
                    <svg className="h-5 w-5 text-amber-500 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <div className="text-sm text-amber-800">
                      <strong>Early matinee detected.</strong> Both class slots will run at the same time so faculty can make the show.
                    </div>
                  </div>
                </div>
              )}

              {/* Session Toggle Cards with Inline Level Dropdowns */}
              <div ref={levelSectionRef} className="grid sm:grid-cols-2 gap-3">
                {CLASS_TIME_SLOTS.map((slot) => {
                  const slotData = selectedDateSlots.find((s) => s.time_slot === slot);
                  const isAvailable = slotData?.available ?? false;
                  const isSession1 = slot === '9:00 AM';
                  const isOn = isSession1 ? session1On : session2On;
                  const info = TIME_SLOT_LABELS[slot];

                  const selectedDateRemaining = dateSummary.get(selectedDate)?.remaining ?? Infinity;

                  const handleToggle = () => {
                    if (!isAvailable) return;
                    isExplicitSlotClick.current = true;

                    let newSession1 = isSession1 ? !session1On : session1On;
                    let newSession2 = isSession1 ? session2On : !session2On;

                    // When only 1 faculty spot remains, switch instead of adding both
                    if (newSession1 && newSession2 && selectedDateRemaining < 2) {
                      if (isSession1) {
                        newSession2 = false;
                        setLevel2('');
                      } else {
                        newSession1 = false;
                        setLevel1('');
                      }
                    }

                    // Clear level when toggling off
                    if (isSession1 && session1On) setLevel1('');
                    if (!isSession1 && session2On) setLevel2('');

                    // Compute effective time slot
                    let effective: TimeSlotSelection | undefined;
                    if (newSession1 && newSession2) effective = 'both';
                    else if (newSession1) effective = '9:00 AM';
                    else if (newSession2) effective = '11:00 AM';

                    if (effective) {
                      const newLevels = {
                        session1: (newSession1 ? (isSession1 && !session1On ? '' : level1) : undefined) || undefined,
                        session2: (newSession2 ? (!isSession1 && !session2On ? '' : level2) : undefined) || undefined,
                      };
                      onSelect(selectedDate, effective, newLevels);
                    } else {
                      setSession1On(false);
                      setSession2On(false);
                      setLevel1('');
                      setLevel2('');
                      onSelect(selectedDate, undefined, {});
                    }
                  };

                  const currentLevel = isSession1 ? level1 : level2;
                  const effectivelyDisabled = !isAvailable;

                  return (
                    <div
                      key={slot}
                      data-testid={isSession1 ? 'session-1-card' : 'session-2-card'}
                      className={`
                        rounded-lg border-2 overflow-hidden transition-all
                        ${isOn && isAvailable
                          ? 'border-primary-500 bg-primary-50/50 ring-2 ring-primary-200'
                          : isAvailable
                            ? 'border-warm-200 hover:border-primary-300'
                            : 'border-warm-100 bg-warm-50 opacity-50'
                        }
                      `}
                    >
                      <button
                        type="button"
                        disabled={effectivelyDisabled}
                        onClick={handleToggle}
                        data-testid={isSession1 ? 'session-1-toggle' : 'session-2-toggle'}
                        className={`
                          w-full p-4 text-left transition-colors relative
                          ${!effectivelyDisabled ? 'cursor-pointer' : 'cursor-not-allowed'}
                          ${isOn && isAvailable ? 'hover:bg-primary-100/50' : !effectivelyDisabled ? 'hover:bg-warm-50' : ''}
                        `}
                      >
                        <div className="flex items-center gap-3">
                          <div className={`
                            w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 transition-colors
                            ${isOn && isAvailable ? 'border-primary-500 bg-primary-500' : 'border-warm-400'}
                          `}>
                            {isOn && isAvailable && (
                              <svg className="h-3 w-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                                <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                              </svg>
                            )}
                          </div>
                          <div>
                            <div className="font-medium text-warm-900">{info.label}</div>
                          </div>
                        </div>
                        {!isAvailable && slotData?.reason && (
                          <div className="text-xs text-warm-400 mt-2 ml-8">{slotData.reason}</div>
                        )}
                      </button>

                      {isOn && isAvailable && (
                        <div className="border-t border-primary-200 bg-white/60 px-4 py-3">
                          <div className="text-xs font-medium text-warm-500 uppercase tracking-wide mb-2">Class Level</div>
                          <div className="flex gap-1.5">
                            {LEVEL_OPTIONS.map((opt) => {
                              const isLevelSelected = currentLevel === opt.value;
                              return (
                                <button
                                  key={opt.value}
                                  type="button"
                                  onClick={() => {
                                    const val = opt.value;
                                    if (isSession1) {
                                      setLevel1(val);
                                      const effective: TimeSlotSelection = session2On ? 'both' : '9:00 AM';
                                      onSelect(selectedDate, effective, { session1: val, session2: level2 || undefined });
                                    } else {
                                      setLevel2(val);
                                      const effective: TimeSlotSelection = session1On ? 'both' : '11:00 AM';
                                      onSelect(selectedDate, effective, { session1: level1 || undefined, session2: val });
                                    }
                                  }}
                                  className={`
                                    flex-1 py-1.5 px-1 rounded-md text-center transition-all text-xs font-medium
                                    ${isLevelSelected
                                      ? 'bg-primary-500 text-white shadow-sm'
                                      : 'bg-warm-100 text-warm-600 hover:bg-warm-200 hover:text-warm-800'
                                    }
                                  `}
                                >
                                  <div>{opt.shortLabel}</div>
                                  <div className={`text-[10px] font-normal mt-0.5 ${isLevelSelected ? 'text-primary-100' : 'text-warm-400'}`}>{opt.ageRange}</div>
                                </button>
                              );
                            })}
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </>
          )}

          {/* ============ MONDAY — Time slot grid grouped by period ============ */}
          {selectedDayType === 'monday' && (() => {
            const getMondayPeriod = (slot: TimeSlot): 'morning' | 'afternoon' | 'evening' => {
              const timeStr = slot.start_time || slot.time_slot;
              const h = parseInt(timeStr.split(':')[0]!, 10) || (timeStr.includes('AM') ? parseInt(timeStr) : parseInt(timeStr) + 12);
              if (h < 12) return 'morning';
              if (h < 17) return 'afternoon';
              return 'evening';
            };
            const morningSlots = mondaySlots.filter(s => getMondayPeriod(s) === 'morning');
            const afternoonSlots = mondaySlots.filter(s => getMondayPeriod(s) === 'afternoon');
            const eveningSlots = mondaySlots.filter(s => getMondayPeriod(s) === 'evening');

            const renderSlotButton = (slot: TimeSlot) => {
              const isSelected = selectedMondaySlot === slot.time_slot;
              const isFull = slot.remaining <= 0 || slot.sold_out;
              const startDisplay = slot.start_time ? formatTimeDisplay(slot.start_time) : slot.time_slot;
              const endDisplay = slot.end_time ? formatTimeDisplay(slot.end_time) : '';

              return (
                <button
                  key={slot.time_slot}
                  type="button"
                  onClick={() => handleMondaySlotSelect(slot)}
                  disabled={isFull}
                  className={`
                    p-3 rounded-lg border-2 text-sm text-left transition-all
                    ${isSelected
                      ? 'border-primary-500 bg-primary-50 ring-2 ring-primary-200'
                      : isFull
                        ? 'border-warm-100 bg-warm-50 opacity-50 cursor-not-allowed'
                        : 'border-warm-200 hover:border-primary-300 cursor-pointer'
                    }
                  `}
                >
                  <p className="font-medium text-warm-900">
                    {startDisplay}{endDisplay ? ` - ${endDisplay}` : ''}
                  </p>
                  <p className={`text-xs mt-1 ${isFull ? 'text-danger-500' : 'text-warm-500'}`}>
                    {isFull ? 'Full' : `${slot.remaining} spot${slot.remaining !== 1 ? 's' : ''} left`}
                  </p>
                </button>
              );
            };

            return (
              <>
                <div className="space-y-4">
                  {morningSlots.length > 0 && (
                    <div>
                      <h5 className="text-xs font-semibold text-warm-500 uppercase tracking-wide mb-2">Morning</h5>
                      <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                        {morningSlots.map(renderSlotButton)}
                      </div>
                    </div>
                  )}
                  {afternoonSlots.length > 0 && (
                    <div>
                      <h5 className="text-xs font-semibold text-warm-500 uppercase tracking-wide mb-2">Afternoon</h5>
                      <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                        {afternoonSlots.map(renderSlotButton)}
                      </div>
                    </div>
                  )}
                  {eveningSlots.length > 0 && (
                    <div>
                      <h5 className="text-xs font-semibold text-warm-500 uppercase tracking-wide mb-2">Evening</h5>
                      <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                        {eveningSlots.map(renderSlotButton)}
                      </div>
                    </div>
                  )}
                </div>

                {mondaySlots.length === 0 && (
                  <p className="text-warm-500 text-center py-4">No time slots available for this Monday.</p>
                )}

                {/* Class level selector — shown after selecting a Monday time slot */}
                {selectedMondaySlot && (
                  <div className="rounded-xl border-2 border-primary-200 bg-primary-50/30 p-4">
                    <div className="text-xs font-medium text-warm-500 uppercase tracking-wide mb-2">Class Level</div>
                    <div className="flex gap-1.5">
                      {LEVEL_OPTIONS.map((opt) => {
                        const isLevelSelected = mondayLevel === opt.value;
                        return (
                          <button
                            key={opt.value}
                            type="button"
                            onClick={() => handleMondayLevelSelect(opt.value)}
                            className={`
                              flex-1 py-1.5 px-1 rounded-md text-center transition-all text-xs font-medium
                              ${isLevelSelected
                                ? 'bg-primary-500 text-white shadow-sm'
                                : 'bg-warm-100 text-warm-600 hover:bg-warm-200 hover:text-warm-800'
                              }
                            `}
                          >
                            <div>{opt.shortLabel}</div>
                            <div className={`text-[10px] font-normal mt-0.5 ${isLevelSelected ? 'text-primary-100' : 'text-warm-400'}`}>{opt.ageRange}</div>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}
              </>
            );
          })()}

          <div className="flex items-center gap-2 text-sm text-warm-500">
            <svg className="h-4 w-4 text-warm-400 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
            </svg>
            <span>Up to 40 students per class</span>
          </div>
          </>
          )}
        </div>
        );
      })()}

      {/* Legend / Info */}
      <div className="space-y-2">
        {useListView ? (
          <p className="text-xs text-warm-400 italic">
            Classes available Saturday, Sunday &amp; Monday
          </p>
        ) : (
          <>
            <div className="flex items-center gap-4 text-xs text-warm-500">
              <div className="flex items-center gap-1">
                <span className="w-3 h-3 rounded-full bg-primary-500" />
                <span>Available</span>
              </div>
              <div className="flex items-center gap-1">
                <span className="w-3 h-3 rounded-full bg-amber-400" />
                <span>Inquiry Only</span>
              </div>
              <div className="flex items-center gap-1">
                <span className="w-3 h-3 rounded-full bg-danger-400" />
                <span>Sold Out</span>
              </div>
              <div className="flex items-center gap-1">
                <span className="w-3 h-3 rounded-full bg-warm-300" />
                <span>Unavailable</span>
              </div>
            </div>
            <p className="text-xs text-warm-400 italic">
              Classes available Saturday, Sunday &amp; Monday
            </p>
          </>
        )}
      </div>
    </div>
  );
}

export default TimeSlotSelector;
